import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Loader2, Search, AlertCircle, Check, ChevronsUpDown, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { getProducts } from "@/integrations/supabase/products";
import { supabase } from "@/integrations/supabase/client";
import { getCurrentEmpresaId } from "@/integrations/supabase/auth-utils";
import { focusNFService } from "@/integrations/focusnf/service";
import { formatCurrency } from "@/utils/formatCurrency";
import { ClientSearch } from "@/components/ClientSearch";
import { listServicos, ServicoRow } from "@/integrations/supabase/servicos";
import {
  criarNFAvulsa,
  ClienteDadosAvulsa,
  gerarDescricaoParcela
} from "@/integrations/supabase/notasFiscaisAvulsas";
import { CFOPsMaisComuns, getDescricaoCFOP, isValidarCFOP } from "@/utils/cfopConstants";
import { CSTsMaisComuns, getDescricaoCST, isValidarCST } from "@/utils/cstConstants";
import { IPISituacoesMaisComuns, getDescricaoIPISituacao, isValidarIPISituacao } from "@/utils/ipiConstants";

interface Cliente {
  id: string;
  nome: string;
  cpf_cnpj: string;
  email?: string;
  telefone?: string;
  endereco?: {
    logradouro?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    uf?: string;
    cep?: string;
  };
}

interface Produto {
  id: string;
  nome: string;
  ncm: string;
  cest: string;
  preco: number;
  fiscal_data?: {
    ncm: string;
    cest: string;
    icms_origem: string;
    icms_aliquota: number;
    pis_aliquota: number;
    cofins_aliquota: number;
    ipi_aliquota: number;
  };
}

interface ServicoSelecionado {
  id: string;
  nome: string;
  descricao?: string | null;
  preco_padrao: number;
  categoria?: string | null;
  item_lista_servico?: string | null;
  codigo_tributario_municipal?: string | null;
  aliquota_nfse?: number | null;
  natureza_operacao?: string | null;
  regime_especial_tributacao?: string | null;
  optante_simples_nacional?: boolean | null;
  incentivador_cultural?: boolean | null;
}

interface DialogEmitirNFAvulsaProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNFEmitida: () => void;
}

export function DialogEmitirNFAvulsa({ open, onOpenChange, onNFEmitida }: DialogEmitirNFAvulsaProps) {
  const [step, setStep] = useState<'cliente' | 'produto' | 'dados_fiscais' | 'parcela' | 'confirmacao'>('cliente');
  const [clienteNome, setClienteNome] = useState('');
  const [clienteSelecionado, setClienteSelecionado] = useState<Cliente | null>(null);
  
  // Recuperar dados do cliente pelo nome quando selecionado
  useEffect(() => {
    if (clienteNome && !clienteSelecionado) {
      // Buscar dados completos do cliente
      const buscarClienteCompleto = async () => {
        try {
          const empresaId = await getCurrentEmpresaId();
          const { data, error } = await supabase
            .from('customers')
            .select('*')
            .eq('empresa_id', empresaId)
            .eq('name', clienteNome)
            .limit(1)
            .maybeSingle();
          
          if (error) {
            console.error('Erro ao buscar cliente:', error);
            return;
          }
          
          if (data) {
            // Mapear campos de 'name' para 'nome' para compatibilidade com interface Cliente
            const clienteMapeado: Cliente = {
              id: data.id,
              nome: data.name,
              cpf_cnpj: data.cpf_cnpj || '',
              email: data.email,
              telefone: data.phone,
              endereco: data.endereco
            };
            setClienteSelecionado(clienteMapeado);
            console.log('Cliente selecionado:', clienteMapeado);
          } else {
            console.warn('Cliente não encontrado para nome:', clienteNome);
          }
        } catch (error) {
          console.error('Erro ao buscar dados do cliente:', error);
        }
      };
      
      buscarClienteCompleto();
    }
  }, [clienteNome]);

  // Estados para produtos (usando padrão de NovoPedido)
  const [produtoSelecionado, setProdutoSelecionado] = useState<Produto | null>(null);
  const [produtoSearchTerm, setProdutoSearchTerm] = useState('');
  const [productPopoverOpen, setProductPopoverOpen] = useState(false);
  const [produtos, setProdutos] = useState<any[]>([]);

  // Buscar produtos do catálogo e serviços
  useEffect(() => {
    const carregarDados = async () => {
      try {
        const data = await getProducts();
        setProdutos(data || []);
      } catch (error) {
        console.error('Erro ao carregar produtos:', error);
      }

      try {
        const servicosData = await listServicos({ ativo: true });
        setServicos(servicosData || []);
      } catch (error) {
        console.error('Erro ao carregar serviços:', error);
      }
    };
    
    if (open) {
      carregarDados();
    }
  }, [open]);

  // Normalizar texto para busca (igual NovoPedido)
  const normalizeSearch = (value: string) =>
    value
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .replace(/\s+/g, " ")
      .trim();

  // Filtrar produtos baseado no termo de busca
  const filteredProducts = useMemo(() => {
    if (!produtoSearchTerm.trim()) {
      return produtos;
    }

    const search = normalizeSearch(produtoSearchTerm);
    return produtos.filter((product) => {
      const base = [
        product.name,
        product.type || "",
        ...(Array.isArray(product.materials) ? product.materials : []),
      ]
        .join(" ")
        .toString();

      const normalizedProduct = normalizeSearch(base);
      return normalizedProduct.includes(search);
    });
  }, [produtos, produtoSearchTerm]);

  // Estados para dados do cliente editáveis
  const [clienteEditar, setClienteEditar] = useState({
    nome: '',
    cpf_cnpj: '',
    email: '',
    telefone: '',
    endereco_logradouro: '',
    endereco_numero: '',
    endereco_complemento: '',
    endereco_bairro: '',
    endereco_cidade: '',
    endereco_uf: 'MG',
    endereco_cep: ''
  });

  // Estados para dados fiscais do produto editáveis
  const [dadosFiscaisEditar, setDadosFiscaisEditar] = useState({
    ncm: '',
    cest: '',
    cfop: '5102',
    cst: '00',
    icms_origem: '0',
    icms_aliquota: 0,
    pis_aliquota: 0,
    cofins_aliquota: 0,
    ipi_situacao_tributaria: '01',  // Situação de IPI (padrão: tributado)
    ipi_aliquota: 0                  // Campo de IPI que o usuário vai preencher
  });

  // Atualizar dados editar quando cliente é selecionado
  useEffect(() => {
    if (clienteSelecionado) {
      setClienteEditar({
        nome: clienteSelecionado.nome,
        cpf_cnpj: clienteSelecionado.cpf_cnpj,
        email: clienteSelecionado.email || '',
        telefone: clienteSelecionado.telefone || '',
        endereco_logradouro: clienteSelecionado.endereco?.logradouro || '',
        endereco_numero: clienteSelecionado.endereco?.numero || '',
        endereco_complemento: clienteSelecionado.endereco?.complemento || '',
        endereco_bairro: clienteSelecionado.endereco?.bairro || '',
        endereco_cidade: clienteSelecionado.endereco?.cidade || '',
        endereco_uf: clienteSelecionado.endereco?.uf || 'MG',
        endereco_cep: clienteSelecionado.endereco?.cep || ''
      });
    }
  }, [clienteSelecionado]);

  // Atualizar dados fiscais quando produto é selecionado
  useEffect(() => {
    if (produtoSelecionado) {
      setDadosFiscaisEditar({
        ncm: produtoSelecionado.ncm || '',
        cest: produtoSelecionado.cest || '',
        icms_origem: produtoSelecionado.fiscal_data?.icms_origem || '0',
        icms_aliquota: produtoSelecionado.fiscal_data?.icms_aliquota || 0,
        pis_aliquota: produtoSelecionado.fiscal_data?.pis_aliquota || 0,
        cofins_aliquota: produtoSelecionado.fiscal_data?.cofins_aliquota || 0,
        ipi_aliquota: produtoSelecionado.fiscal_data?.ipi_aliquota || 0
      });
    }
  }, [produtoSelecionado]);

  const [quantidade, setQuantidade] = useState('1');
  const [valorUnitario, setValorUnitario] = useState('');
  const [descricao, setDescricao] = useState('');
  const [tipoNota, setTipoNota] = useState<'NFe' | 'NFCe' | 'NFSe'>('NFe');

  const [servicoSelecionado, setServicoSelecionado] = useState<ServicoSelecionado | null>(null);
  const [servicoSearchTerm, setServicoSearchTerm] = useState('');
  const [servicoPopoverOpen, setServicoPopoverOpen] = useState(false);
  const [servicos, setServicos] = useState<ServicoRow[]>([]);

  const filteredServicos = useMemo(() => {
    if (!servicoSearchTerm.trim()) {
      return servicos;
    }

    const search = normalizeSearch(servicoSearchTerm);
    return servicos.filter((servico) => {
      const base = [servico.nome, servico.descricao || '', servico.categoria || '']
        .join(' ')
        .toString();

      const normalizedServico = normalizeSearch(base);
      return normalizedServico.includes(search);
    });
  }, [servicos, servicoSearchTerm]);

  const normalizarCodigoTributarioMunicipio = (value: string) => {
    const digits = String(value || '').replace(/\D/g, '');
    return digits.substring(0, 3);
  };

  const aplicarDadosFiscaisServico = (servico: ServicoSelecionado) => {
    setNfseDados((prev) => ({
      ...prev,
      itemListaServico: servico.item_lista_servico || prev.itemListaServico,
      codigoTributarioMunicipio: normalizarCodigoTributarioMunicipio(
        servico.codigo_tributario_municipal || prev.codigoTributarioMunicipio
      ),
      aliquota: servico.aliquota_nfse != null
        ? servico.aliquota_nfse.toString()
        : prev.aliquota,
      naturezaOperacao: servico.natureza_operacao || prev.naturezaOperacao || '1',
      regimeEspecialTributacao: servico.regime_especial_tributacao || prev.regimeEspecialTributacao,
      optanteSimplesNacional: servico.optante_simples_nacional ?? prev.optanteSimplesNacional,
      incentivadorCultural: servico.incentivador_cultural ?? prev.incentivadorCultural,
      discriminacao: prev.discriminacao || servico.descricao || servico.nome
    }));
  };

  const [nfseDados, setNfseDados] = useState({
    prestadorCodigoMunicipio: '',
    prestadorInscricaoMunicipal: '',
    tomadorCodigoMunicipio: '',
    codigoMunicipioPrestacao: '',
    itemListaServico: '',
    codigoTributarioMunicipio: '',
    aliquota: '',
    issRetido: false,
    naturezaOperacao: '1',
    regimeEspecialTributacao: '',
    optanteSimplesNacional: true,
    incentivadorCultural: false,
    discriminacao: ''
  });

  const [numeroParcela, setNumeroParcela] = useState('1');
  const [totalParcelas, setTotalParcelas] = useState('1');
  const [emitindo, setEmitindo] = useState(false);

  const nfseCamposObrigatoriosOk = useMemo(() => {
    if (tipoNota !== 'NFSe') return true;
    return Boolean(
      nfseDados.prestadorCodigoMunicipio &&
      nfseDados.prestadorInscricaoMunicipal &&
      nfseDados.tomadorCodigoMunicipio &&
      nfseDados.codigoMunicipioPrestacao &&
      nfseDados.itemListaServico &&
      nfseDados.codigoTributarioMunicipio &&
      nfseDados.aliquota &&
      nfseDados.discriminacao
    );
  }, [tipoNota, nfseDados]);

  const calcularValorTotal = () => {
    const baseUnitario = tipoNota === 'NFSe'
      ? servicoSelecionado?.preco_padrao
      : produtoSelecionado?.preco;
    const unitario = parseFloat(valorUnitario || baseUnitario?.toString() || '0');
    const qtd = parseFloat(quantidade || '0');
    return unitario * qtd;
  };

  const handleEmitir = async () => {
    if (!clienteSelecionado) {
      toast.error('Selecione um cliente');
      return;
    }

    if (tipoNota === 'NFSe') {
      if (!servicoSelecionado) {
        toast.error('Selecione um serviço');
        return;
      }
    } else if (!produtoSelecionado) {
      toast.error('Selecione um produto');
      return;
    }

    if (!quantidade || parseFloat(quantidade) <= 0) {
      toast.error('Quantidade inválida');
      return;
    }

    const valorTotal = calcularValorTotal();
    if (valorTotal <= 0) {
      toast.error('Valor total inválido');
      return;
    }

    // Validar dados fiscais (somente NFe/NFCe)
    if (tipoNota !== 'NFSe') {
      if (!dadosFiscaisEditar.ncm || dadosFiscaisEditar.ncm.length < 8) {
        toast.error('NCM inválido. Deve ter 8 dígitos.');
        return;
      }
    }

    // Validar cliente editado
    if (!clienteEditar.nome.trim()) {
      toast.error('Nome do cliente é obrigatório');
      return;
    }

    const cpfCnpjLimpo = clienteEditar.cpf_cnpj.replace(/\D/g, '');
    if (cpfCnpjLimpo.length !== 11 && cpfCnpjLimpo.length !== 14) {
      toast.error('CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos');
      return;
    }

    try {
      setEmitindo(true);
      const empresaId = await getCurrentEmpresaId();

      // Gerar orderCode único para esta NF avulsa (max 50 chars para VARCHAR(50))
      // Formato: AVULSA-TIMESTAMP (mais compacto)
      const orderCode = `AVULSA-${Date.now()}`;

      // Preparar endereco com valores do cliente editado
      const enderecoCompleto = {
        logradouro: clienteEditar.endereco_logradouro || '',
        numero: clienteEditar.endereco_numero || '',
        complemento: clienteEditar.endereco_complemento || '',
        bairro: clienteEditar.endereco_bairro || '',
        cidade: clienteEditar.endereco_cidade || '',
        uf: clienteEditar.endereco_uf || 'MG',
        cep: clienteEditar.endereco_cep || ''
      };

      let itemsNota: any[] | undefined;
      let nfsePayload: any | undefined;

      if (tipoNota === 'NFSe') {
        if (!nfseDados.prestadorCodigoMunicipio.trim()) {
          toast.error('Código do município do prestador é obrigatório');
          return;
        }
        if (!nfseDados.prestadorInscricaoMunicipal.trim()) {
          toast.error('Inscrição municipal do prestador é obrigatória');
          return;
        }
        if (!nfseDados.tomadorCodigoMunicipio.trim()) {
          toast.error('Código do município do tomador é obrigatório');
          return;
        }
        if (!nfseDados.itemListaServico.trim()) {
          toast.error('Item da lista de serviço é obrigatório');
          return;
        }
        if (!nfseDados.aliquota.trim() || Number.isNaN(Number(nfseDados.aliquota))) {
          toast.error('Alíquota do ISS é obrigatória');
          return;
        }

        const discriminacao =
          nfseDados.discriminacao ||
          descricao ||
          servicoSelecionado?.descricao ||
          servicoSelecionado?.nome ||
          'Serviço prestado';

        nfsePayload = {
          prestadorCodigoMunicipio: nfseDados.prestadorCodigoMunicipio.trim(),
          prestadorInscricaoMunicipal: nfseDados.prestadorInscricaoMunicipal.trim(),
          tomadorCodigoMunicipio: nfseDados.tomadorCodigoMunicipio.trim(),
          codigoMunicipioPrestacao: nfseDados.codigoMunicipioPrestacao.trim() || nfseDados.tomadorCodigoMunicipio.trim(),
          itemListaServico: nfseDados.itemListaServico.trim(),
          codigoTributarioMunicipio:
            normalizarCodigoTributarioMunicipio(nfseDados.codigoTributarioMunicipio.trim()) || undefined,
          aliquota: Number(nfseDados.aliquota),
          issRetido: nfseDados.issRetido,
          discriminacao,
          naturezaOperacao: Number(nfseDados.naturezaOperacao || '1'),
          regimeEspecialTributacao: nfseDados.regimeEspecialTributacao.trim()
            ? Number(nfseDados.regimeEspecialTributacao)
            : undefined,
          optanteSimplesNacional: nfseDados.optanteSimplesNacional,
          incentivadorCultural: nfseDados.incentivadorCultural
        };
      } else {
        // Validar CFOP
        let cfopValido = dadosFiscaisEditar.cfop;
        if (!isValidarCFOP(cfopValido)) {
          cfopValido = '5102';
        }

        // Validar CST
        let cstValido = dadosFiscaisEditar.cst;
        if (!isValidarCST(cstValido)) {
          cstValido = '00';
        }

        // Montar item com dados opcionais
        const item: any = {
          numero_item: 1,
          codigo_produto: produtoSelecionado.id,
          descricao: descricao || produtoSelecionado.nome,
          ncm: dadosFiscaisEditar.ncm,
          cfop: cfopValido,
          icms_situacao_tributaria: cstValido,
          unidade: 'UN',
          quantidade: parseFloat(quantidade),
          valor_unitario: parseFloat(valorUnitario || produtoSelecionado.preco.toString()),
          valor_total: valorTotal
        };

        // Adicionar dados fiscais opcionais se fornecidos
        if (dadosFiscaisEditar.icms_aliquota) {
          item.icms_aliquota = dadosFiscaisEditar.icms_aliquota;
        }
        if (dadosFiscaisEditar.pis_aliquota) {
          item.pis_aliquota = dadosFiscaisEditar.pis_aliquota;
        }
        if (dadosFiscaisEditar.cofins_aliquota) {
          item.cofins_aliquota = dadosFiscaisEditar.cofins_aliquota;
        }
        if (dadosFiscaisEditar.ipi_aliquota) {
          item.ipi_aliquota = dadosFiscaisEditar.ipi_aliquota;
        }

        itemsNota = [item];
      }

      // Emitir nota via Focus NF
      // Usar dados editados do cliente e dados fiscais editados do produto/serviço
      const resultado = await focusNFService.emitirNota({
        orderCode,
        tipoNota,
        cliente: {
          nome: clienteEditar.nome,
          cpf_cnpj: cpfCnpjLimpo,
          email: clienteEditar.email || undefined,
          telefone: clienteEditar.telefone || undefined,
          endereco: enderecoCompleto
        },
        items: itemsNota,
        valorTotal: valorTotal,
        nfse: nfsePayload
      });

      if (resultado.ok) {
        // A nota foi criada com sucesso pelo focusNFService
        // Não é necessário inserir novamente - o serviço já faz isso internamente
        
        toast.success(`✅ NF avulsa emitida com sucesso! Parcela ${numeroParcela}/${totalParcelas}`);
        onNFEmitida();
        handleReset();
        onOpenChange(false);
      } else {
        toast.error(resultado.error || 'Erro ao emitir nota fiscal');
      }
    } catch (error) {
      console.error('Erro ao emitir NF avulsa:', error);
      toast.error('Erro ao emitir nota fiscal');
    } finally {
      setEmitindo(false);
    }
  };

  const handleReset = () => {
    setStep('cliente');
    setClienteNome('');
    setClienteSelecionado(null);
    setProdutoSelecionado(null);
    setServicoSelecionado(null);
    setProdutoSearchTerm('');
    setProductPopoverOpen(false);
    setServicoSearchTerm('');
    setServicoPopoverOpen(false);
    setQuantidade('1');
    setValorUnitario('');
    setDescricao('');
    setNumeroParcela('1');
    setTotalParcelas('1');
    setTipoNota('NFe');
    setNfseDados({
      prestadorCodigoMunicipio: '',
      prestadorInscricaoMunicipal: '',
      tomadorCodigoMunicipio: '',
      codigoMunicipioPrestacao: '',
      itemListaServico: '',
      codigoTributarioMunicipio: '',
      aliquota: '',
      issRetido: false,
      naturezaOperacao: '1',
      regimeEspecialTributacao: '',
      optanteSimplesNacional: true,
      incentivadorCultural: false,
      discriminacao: ''
    });
  };

  const itemSelecionado = tipoNota === 'NFSe' ? servicoSelecionado : produtoSelecionado;

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      if (!newOpen) handleReset();
      onOpenChange(newOpen);
    }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Emitir Nota Fiscal Avulsa</DialogTitle>
          <DialogDescription>
            Emitir uma nota fiscal sem vincular a um pedido. Perfeito para emissão de parcelas.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Indicador de progresso */}
          <div className="flex gap-2">
            <Badge variant={step === 'cliente' ? 'default' : 'secondary'}>1. Cliente</Badge>
            <Badge variant={step === 'produto' ? 'default' : 'secondary'}>
              2. {tipoNota === 'NFSe' ? 'Serviço' : 'Produto'}
            </Badge>
            <Badge variant={step === 'dados_fiscais' ? 'default' : 'secondary'}>3. Dados Fiscais</Badge>
            <Badge variant={step === 'parcela' ? 'default' : 'secondary'}>4. Parcela</Badge>
            <Badge variant={step === 'confirmacao' ? 'default' : 'secondary'}>5. Confirmar</Badge>
          </div>

          {/* STEP 1: CLIENTE */}
          {step === 'cliente' && (
            <div className="space-y-4">
              <Card className="border-blue-200 bg-blue-50">
                <CardHeader>
                  <CardTitle className="text-base">Tipo de Nota</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label htmlFor="tipo-nota">Selecione o modelo</Label>
                    <Select value={tipoNota} onValueChange={(value: 'NFe' | 'NFCe' | 'NFSe') => setTipoNota(value)}>
                      <SelectTrigger id="tipo-nota" className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NFe">NF-e (Modelo 55)</SelectItem>
                        <SelectItem value="NFCe">NFC-e (Modelo 65)</SelectItem>
                        <SelectItem value="NFSe">NFS-e (Serviços)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-blue-700">
                    {tipoNota === 'NFSe'
                      ? 'NFS-e exige dados municipais (IBGE, item da lista de serviço e inscrição municipal).'
                      : tipoNota === 'NFCe'
                      ? 'NFC-e exige CSC e configurações habilitadas no SEFAZ. Se faltar algo, o sistema vai avisar.'
                      : 'NF-e é o modelo padrão e mantém o fluxo atual de emissão.'}
                  </p>
                </CardContent>
              </Card>

              <div className="space-y-2">
                <ClientSearch
                  value={clienteNome}
                  onChange={setClienteNome}
                  placeholder="Buscar cliente por nome, CPF ou CNPJ..."
                />
              </div>

              {clienteSelecionado && (
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="text-green-800 font-semibold flex items-center gap-2">
                          <span>✓</span> Cliente selecionado
                        </div>
                        <div className="font-medium mt-1">{clienteSelecionado.nome}</div>
                        <div className="text-sm text-green-700">{clienteSelecionado.cpf_cnpj}</div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setClienteSelecionado(null)}
                      >
                        Alterar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Button 
                onClick={() => setStep('produto')} 
                disabled={!clienteSelecionado}
                className="w-full"
              >
                Próximo: Selecionar {tipoNota === 'NFSe' ? 'Serviço' : 'Produto'} →
              </Button>
            </div>
          )}

          {/* STEP 2: PRODUTO */}
          {step === 'produto' && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="space-y-3">
                {tipoNota === 'NFSe' ? (
                  <>
                    <Label>Selecionar Serviço</Label>
                    <div className="flex gap-3">
                      <Popover open={servicoPopoverOpen} onOpenChange={setServicoPopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={servicoPopoverOpen}
                            className="flex-1 justify-between"
                          >
                            {servicoSelecionado
                              ? servicos.find((servico) => servico.id === servicoSelecionado.id)?.nome || "Selecione um serviço"
                              : "Selecione um serviço"}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        {servicoSelecionado && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setServicoSelecionado(null);
                              setServicoSearchTerm("");
                            }}
                            className="shrink-0"
                            title="Remover seleção"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                        <PopoverContent className="w-[400px] p-0" align="start">
                          <Command>
                            <CommandInput 
                              placeholder="Pesquisar serviço por nome, descrição ou categoria..." 
                              value={servicoSearchTerm}
                              onValueChange={setServicoSearchTerm}
                            />
                            <CommandList>
                              <CommandEmpty>Nenhum serviço encontrado.</CommandEmpty>
                              <CommandGroup>
                                {filteredServicos.map((servico) => (
                                  <CommandItem
                                    key={servico.id}
                                    value={`${servico.nome} ${servico.descricao || ""} ${servico.categoria || ""}`}
                                    onSelect={() => {
                                      const servicoMapeado: ServicoSelecionado = {
                                        id: servico.id,
                                        nome: servico.nome,
                                        descricao: servico.descricao,
                                        preco_padrao: servico.preco_padrao || 0,
                                        categoria: servico.categoria,
                                        item_lista_servico: servico.item_lista_servico,
                                        codigo_tributario_municipal: servico.codigo_tributario_municipal,
                                        aliquota_nfse: servico.aliquota_nfse,
                                        natureza_operacao: servico.natureza_operacao,
                                        regime_especial_tributacao: servico.regime_especial_tributacao,
                                        optante_simples_nacional: servico.optante_simples_nacional,
                                        incentivador_cultural: servico.incentivador_cultural
                                      };
                                      setServicoSelecionado(servicoMapeado);
                                      setValorUnitario(servico.preco_padrao?.toString() || '0');
                                      aplicarDadosFiscaisServico(servicoMapeado);
                                      setServicoPopoverOpen(false);
                                      setServicoSearchTerm("");
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        servicoSelecionado?.id === servico.id ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    <div className="flex flex-col flex-1">
                                      <span className="font-medium">{servico.nome}</span>
                                      <span className="text-xs text-muted-foreground">
                                        R$ {(servico.preco_padrao || 0).toFixed(2)}
                                        {servico.categoria && ` • ${servico.categoria}`}
                                      </span>
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </>
                ) : (
                  <>
                    <Label>Selecionar Produto do Catálogo</Label>
                    <div className="flex gap-3">
                      <Popover open={productPopoverOpen} onOpenChange={setProductPopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={productPopoverOpen}
                            className="flex-1 justify-between"
                          >
                            {produtoSelecionado
                              ? produtos.find((product) => product.id === produtoSelecionado.id)?.name || "Selecione um produto"
                              : "Selecione um produto do catálogo"}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        {produtoSelecionado && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setProdutoSelecionado(null);
                              setProdutoSearchTerm("");
                            }}
                            className="shrink-0"
                            title="Remover seleção"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                        <PopoverContent className="w-[400px] p-0" align="start">
                          <Command>
                            <CommandInput 
                              placeholder="Pesquisar produto por nome, tipo ou material..." 
                              value={produtoSearchTerm}
                              onValueChange={setProdutoSearchTerm}
                            />
                            <CommandList>
                              <CommandEmpty>Nenhum produto encontrado.</CommandEmpty>
                              <CommandGroup>
                                {filteredProducts.map((product) => (
                                  <CommandItem
                                    key={product.id}
                                    value={`${product.name} ${product.type || ""} ${product.materials?.join(" ") || ""}`}
                                    onSelect={() => {
                                      // Converter para interface Produto
                                      const produtoMapeado: Produto = {
                                        id: product.id,
                                        nome: product.name,
                                        ncm: product.fiscal_data?.ncm_code || '6105.10.00',
                                        cest: product.fiscal_data?.cest_code || '',
                                        preco: product.unit_price || 0,
                                        fiscal_data: product.fiscal_data
                                      };
                                      setProdutoSelecionado(produtoMapeado);
                                      setValorUnitario(product.unit_price?.toString() || '0');
                                      setProductPopoverOpen(false);
                                      setProdutoSearchTerm("");
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        produtoSelecionado?.id === product.id ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    <div className="flex flex-col flex-1">
                                      <span className="font-medium">{product.name}</span>
                                      <span className="text-xs text-muted-foreground">
                                        R$ {(product.unit_price || 0).toFixed(2)}
                                        {product.type && ` • ${product.type}`}
                                      </span>
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </>
                )}
              </div>

              {itemSelecionado && (
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="text-green-800 font-semibold flex items-center gap-2">
                          <span>✓</span> {tipoNota === 'NFSe' ? 'Serviço selecionado' : 'Produto selecionado'}
                        </div>
                        <div className="font-medium mt-1">{itemSelecionado.nome}</div>
                        <div className="text-sm text-green-700">
                          {tipoNota === 'NFSe'
                            ? formatCurrency({ value: itemSelecionado.preco })
                            : `NCM: ${itemSelecionado.ncm} | ${formatCurrency({ value: itemSelecionado.preco })}`}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          if (tipoNota === 'NFSe') {
                            setServicoSelecionado(null);
                          } else {
                            setProdutoSelecionado(null);
                          }
                        }}
                      >
                        Alterar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {itemSelecionado && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="quantidade">Quantidade</Label>
                    <Input
                      id="quantidade"
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={quantidade}
                      onChange={(e) => setQuantidade(e.target.value)}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="valor-unitario">Valor Unitário</Label>
                    <Input
                      id="valor-unitario"
                      type="number"
                      min="0"
                      step="0.01"
                      value={valorUnitario}
                      onChange={(e) => setValorUnitario(e.target.value)}
                      className="mt-2"
                    />
                  </div>
                </div>
              )}

              {itemSelecionado && quantidade && valorUnitario && (
                <Card className="bg-gray-50 border-gray-200">
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-center">
                      <span>Valor Total:</span>
                      <span className="text-lg font-bold text-green-600">
                        {formatCurrency({ value: calcularValorTotal() })}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => setStep('cliente')}
                  className="w-full"
                >
                  Voltar
                </Button>
                <Button 
                  onClick={() => setStep('dados_fiscais')}
                  disabled={!itemSelecionado || !quantidade || !valorUnitario}
                  className="w-full"
                >
                  Próximo: Dados Fiscais
                </Button>
              </div>
            </div>
          )}

          {/* STEP 3: DADOS FISCAIS E CLIENTE */}
          {step === 'dados_fiscais' && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Dados do Cliente */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Editar Dados do Cliente</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="cliente-nome">Nome *</Label>
                      <Input
                        id="cliente-nome"
                        value={clienteEditar.nome}
                        onChange={(e) => setClienteEditar({...clienteEditar, nome: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="cliente-cpf">CPF/CNPJ *</Label>
                      <Input
                        id="cliente-cpf"
                        value={clienteEditar.cpf_cnpj}
                        onChange={(e) => setClienteEditar({...clienteEditar, cpf_cnpj: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="cliente-email">Email</Label>
                      <Input
                        id="cliente-email"
                        type="email"
                        value={clienteEditar.email}
                        onChange={(e) => setClienteEditar({...clienteEditar, email: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="cliente-telefone">Telefone</Label>
                      <Input
                        id="cliente-telefone"
                        value={clienteEditar.telefone}
                        onChange={(e) => setClienteEditar({...clienteEditar, telefone: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-3 text-sm">Endereço</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label htmlFor="endereco-logradouro">Logradouro</Label>
                        <Input
                          id="endereco-logradouro"
                          value={clienteEditar.endereco_logradouro}
                          onChange={(e) => setClienteEditar({...clienteEditar, endereco_logradouro: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="endereco-numero">Número</Label>
                        <Input
                          id="endereco-numero"
                          value={clienteEditar.endereco_numero}
                          onChange={(e) => setClienteEditar({...clienteEditar, endereco_numero: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="endereco-complemento">Complemento</Label>
                        <Input
                          id="endereco-complemento"
                          value={clienteEditar.endereco_complemento}
                          onChange={(e) => setClienteEditar({...clienteEditar, endereco_complemento: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="endereco-bairro">Bairro</Label>
                        <Input
                          id="endereco-bairro"
                          value={clienteEditar.endereco_bairro}
                          onChange={(e) => setClienteEditar({...clienteEditar, endereco_bairro: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="endereco-cidade">Cidade</Label>
                        <Input
                          id="endereco-cidade"
                          value={clienteEditar.endereco_cidade}
                          onChange={(e) => setClienteEditar({...clienteEditar, endereco_cidade: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="endereco-uf">UF</Label>
                        <Input
                          id="endereco-uf"
                          value={clienteEditar.endereco_uf}
                          maxLength={2}
                          onChange={(e) => setClienteEditar({...clienteEditar, endereco_uf: e.target.value.toUpperCase()})}
                          className="mt-1"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label htmlFor="endereco-cep">CEP</Label>
                        <Input
                          id="endereco-cep"
                          value={clienteEditar.endereco_cep}
                          onChange={(e) => setClienteEditar({...clienteEditar, endereco_cep: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Dados Fiscais do Produto / Serviço */}
              {tipoNota === 'NFSe' ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Dados Fiscais do Serviço (NFS-e)</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {servicoSelecionado && (
                      <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 p-3">
                        <p className="text-xs text-blue-800">
                          Serviço selecionado: <strong>{servicoSelecionado.nome}</strong>
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            aplicarDadosFiscaisServico(servicoSelecionado);
                            toast.success('Dados fiscais do serviço aplicados');
                          }}
                        >
                          Preencher com serviço
                        </Button>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="prestador-codigo-municipio">Município IBGE do Prestador *</Label>
                        <Input
                          id="prestador-codigo-municipio"
                          value={nfseDados.prestadorCodigoMunicipio}
                          onChange={(e) => setNfseDados({ ...nfseDados, prestadorCodigoMunicipio: e.target.value })}
                          placeholder="Ex: 3538901"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="prestador-inscricao-municipal">Inscrição Municipal do Prestador *</Label>
                        <Input
                          id="prestador-inscricao-municipal"
                          value={nfseDados.prestadorInscricaoMunicipal}
                          onChange={(e) => setNfseDados({ ...nfseDados, prestadorInscricaoMunicipal: e.target.value })}
                          placeholder="Ex: 123456"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="tomador-codigo-municipio">Município IBGE do Tomador *</Label>
                        <Input
                          id="tomador-codigo-municipio"
                          value={nfseDados.tomadorCodigoMunicipio}
                          onChange={(e) => setNfseDados({ ...nfseDados, tomadorCodigoMunicipio: e.target.value })}
                          placeholder="Ex: 3169007"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="codigo-municipio-prestacao">Município IBGE da Prestação *</Label>
                        <Input
                          id="codigo-municipio-prestacao"
                          value={nfseDados.codigoMunicipioPrestacao}
                          onChange={(e) => setNfseDados({ ...nfseDados, codigoMunicipioPrestacao: e.target.value })}
                          placeholder="Ex: 3538901"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="item-lista-servico">Item da Lista de Serviço *</Label>
                        <Input
                          id="item-lista-servico"
                          value={nfseDados.itemListaServico}
                          onChange={(e) => setNfseDados({ ...nfseDados, itemListaServico: e.target.value })}
                          placeholder="Ex: 1401"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="codigo-tributario">Código Tributário do Município *</Label>
                        <Input
                          id="codigo-tributario"
                          value={nfseDados.codigoTributarioMunicipio}
                          onChange={(e) =>
                            setNfseDados({
                              ...nfseDados,
                              codigoTributarioMunicipio: normalizarCodigoTributarioMunicipio(e.target.value)
                            })
                          }
                          placeholder="Ex: 140"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="aliquota">Alíquota (%) *</Label>
                        <Input
                          id="aliquota"
                          type="number"
                          step="0.01"
                          value={nfseDados.aliquota}
                          onChange={(e) => setNfseDados({ ...nfseDados, aliquota: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="iss-retido">ISS Retido *</Label>
                        <Select
                          value={nfseDados.issRetido ? 'true' : 'false'}
                          onValueChange={(v) => setNfseDados({ ...nfseDados, issRetido: v === 'true' })}
                        >
                          <SelectTrigger id="iss-retido" className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="false">Não</SelectItem>
                            <SelectItem value="true">Sim</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="natureza-operacao">Natureza da Operação</Label>
                        <Input
                          id="natureza-operacao"
                          value={nfseDados.naturezaOperacao}
                          onChange={(e) => setNfseDados({ ...nfseDados, naturezaOperacao: e.target.value })}
                          placeholder="Ex: 1"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="regime-especial">Regime Especial de Tributação</Label>
                        <Input
                          id="regime-especial"
                          value={nfseDados.regimeEspecialTributacao}
                          onChange={(e) => setNfseDados({ ...nfseDados, regimeEspecialTributacao: e.target.value })}
                          placeholder="Ex: 6"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="optante-simples">Optante Simples Nacional</Label>
                        <Select
                          value={nfseDados.optanteSimplesNacional ? 'true' : 'false'}
                          onValueChange={(v) => setNfseDados({ ...nfseDados, optanteSimplesNacional: v === 'true' })}
                        >
                          <SelectTrigger id="optante-simples" className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="true">Sim</SelectItem>
                            <SelectItem value="false">Não</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="incentivador-cultural">Incentivador Cultural</Label>
                        <Select
                          value={nfseDados.incentivadorCultural ? 'true' : 'false'}
                          onValueChange={(v) => setNfseDados({ ...nfseDados, incentivadorCultural: v === 'true' })}
                        >
                          <SelectTrigger id="incentivador-cultural" className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="false">Não</SelectItem>
                            <SelectItem value="true">Sim</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="discriminacao">Discriminação do Serviço *</Label>
                      <Textarea
                        id="discriminacao"
                        value={nfseDados.discriminacao}
                        onChange={(e) => setNfseDados({ ...nfseDados, discriminacao: e.target.value })}
                        placeholder="Descreva o serviço prestado"
                        className="mt-1"
                      />
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Editar Dados Fiscais do Produto</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="ncm">NCM *</Label>
                        <Input
                          id="ncm"
                          value={dadosFiscaisEditar.ncm}
                          onChange={(e) => setDadosFiscaisEditar({...dadosFiscaisEditar, ncm: e.target.value})}
                          placeholder="00000000"
                          maxLength={8}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="cest">CEST</Label>
                        <Input
                          id="cest"
                          value={dadosFiscaisEditar.cest}
                          onChange={(e) => setDadosFiscaisEditar({...dadosFiscaisEditar, cest: e.target.value})}
                          placeholder="0000000"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="cfop">CFOP *</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              className="w-full justify-between mt-1"
                            >
                              {dadosFiscaisEditar.cfop ? (
                                `${dadosFiscaisEditar.cfop} - ${getDescricaoCFOP(dadosFiscaisEditar.cfop).substring(0, 30)}...`
                              ) : (
                                "Selecione CFOP"
                              )}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[400px] p-0">
                            <Command>
                              <CommandInput placeholder="Pesquisar CFOP..." />
                              <CommandEmpty>CFOP não encontrado</CommandEmpty>
                              <CommandGroup>
                                <CommandList>
                                  {CFOPsMaisComuns.map((cfop) => (
                                    <CommandItem
                                      key={cfop.codigo}
                                      value={cfop.codigo}
                                      onSelect={(currentValue) => {
                                        setDadosFiscaisEditar({
                                          ...dadosFiscaisEditar,
                                          cfop: currentValue === dadosFiscaisEditar.cfop ? '' : currentValue
                                        });
                                      }}
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4",
                                          dadosFiscaisEditar.cfop === cfop.codigo ? "opacity-100" : "opacity-0"
                                        )}
                                      />
                                      <div className="flex flex-col">
                                        <span className="font-medium">{cfop.codigo}</span>
                                        <span className="text-sm text-gray-600">{cfop.descricao}</span>
                                      </div>
                                    </CommandItem>
                                  ))}
                                </CommandList>
                              </CommandGroup>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        {dadosFiscaisEditar.cfop && (
                          <p className="text-xs text-gray-500 mt-1">
                            {getDescricaoCFOP(dadosFiscaisEditar.cfop)}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="cst">CST (Situação Tributária) *</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              className="w-full justify-between mt-1"
                            >
                              {dadosFiscaisEditar.cst ? (
                                `${dadosFiscaisEditar.cst} - ${getDescricaoCST(dadosFiscaisEditar.cst).substring(0, 30)}...`
                              ) : (
                                "Selecione CST"
                              )}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[450px] p-0">
                            <Command>
                              <CommandInput placeholder="Pesquisar CST (ex: 00, 41, 102)..." />
                              <CommandEmpty>CST não encontrado</CommandEmpty>
                              <CommandGroup>
                                <CommandList>
                                  {CSTsMaisComuns.map((cst) => (
                                    <CommandItem
                                      key={cst.codigo}
                                      value={cst.codigo}
                                      onSelect={(currentValue) => {
                                        setDadosFiscaisEditar({
                                          ...dadosFiscaisEditar,
                                          cst: currentValue === dadosFiscaisEditar.cst ? '' : currentValue
                                        });
                                      }}
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4",
                                          dadosFiscaisEditar.cst === cst.codigo ? "opacity-100" : "opacity-0"
                                        )}
                                      />
                                      <div className="flex flex-col">
                                        <span className="font-medium">{cst.codigo}</span>
                                        <span className="text-sm text-gray-600">{cst.descricao}</span>
                                      </div>
                                    </CommandItem>
                                  ))}
                                </CommandList>
                              </CommandGroup>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        {dadosFiscaisEditar.cst && (
                          <p className="text-xs text-gray-500 mt-1">
                            {getDescricaoCST(dadosFiscaisEditar.cst)}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="icms_origem">ICMS Origem *</Label>
                        <Select value={dadosFiscaisEditar.icms_origem} onValueChange={(v) => setDadosFiscaisEditar({...dadosFiscaisEditar, icms_origem: v})}>
                          <SelectTrigger id="icms_origem" className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">Nacional</SelectItem>
                            <SelectItem value="1">Estrangeira</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="icms_aliquota">ICMS Alíquota (%)</Label>
                        <Input
                          id="icms_aliquota"
                          type="number"
                          step="0.01"
                          value={dadosFiscaisEditar.icms_aliquota}
                          onChange={(e) => setDadosFiscaisEditar({...dadosFiscaisEditar, icms_aliquota: parseFloat(e.target.value) || 0})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="pis_aliquota">PIS Alíquota (%)</Label>
                        <Input
                          id="pis_aliquota"
                          type="number"
                          step="0.01"
                          value={dadosFiscaisEditar.pis_aliquota}
                          onChange={(e) => setDadosFiscaisEditar({...dadosFiscaisEditar, pis_aliquota: parseFloat(e.target.value) || 0})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="cofins_aliquota">COFINS Alíquota (%)</Label>
                        <Input
                          id="cofins_aliquota"
                          type="number"
                          step="0.01"
                          value={dadosFiscaisEditar.cofins_aliquota}
                          onChange={(e) => setDadosFiscaisEditar({...dadosFiscaisEditar, cofins_aliquota: parseFloat(e.target.value) || 0})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="ipi_situacao">IPI - Situação</Label>
                        <Select
                          value={dadosFiscaisEditar.ipi_situacao_tributaria}
                          onValueChange={(value) => {
                            if (isValidarIPISituacao(value)) {
                              setDadosFiscaisEditar({...dadosFiscaisEditar, ipi_situacao_tributaria: value});
                            }
                          }}
                        >
                          <SelectTrigger id="ipi_situacao" className="mt-2 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {IPISituacoesMaisComuns.map((situacao) => (
                              <SelectItem key={situacao.codigo} value={situacao.codigo}>
                                {situacao.codigo} - {situacao.descricao}
                              </SelectItem>
                            ))}
                            <div className="px-2 py-1.5 text-xs text-gray-500 border-t">
                              Consulte seu contador
                            </div>
                          </SelectContent>
                        </Select>
                        {dadosFiscaisEditar.ipi_situacao_tributaria && (
                          <p className="text-xs text-gray-500 mt-1">
                            {getDescricaoIPISituacao(dadosFiscaisEditar.ipi_situacao_tributaria)}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="ipi_aliquota">IPI Alíquota (%)</Label>
                        <Input
                          id="ipi_aliquota"
                          type="number"
                          step="0.01"
                          value={dadosFiscaisEditar.ipi_aliquota}
                          onChange={(e) => setDadosFiscaisEditar({...dadosFiscaisEditar, ipi_aliquota: parseFloat(e.target.value) || 0})}
                          placeholder="Ex: 5, 10, 15"
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Consulte seu contador ou a TIPI (Receita Federal)
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => setStep('produto')}
                  className="w-full"
                >
                  Voltar
                </Button>
                <Button 
                  onClick={() => setStep('parcela')}
                  disabled={!nfseCamposObrigatoriosOk}
                  className="w-full"
                >
                  Próximo: Parcela
                </Button>
              </div>
            </div>
          )}

          {/* STEP 4: PARCELA */}
          {step === 'parcela' && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <Card className="border-blue-200 bg-blue-50">
                <CardHeader>
                  <CardTitle className="text-base">Informações da Parcela</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-700">
                    Configure em qual parcela esta nota fiscal será referente.
                  </p>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="numero-parcela">Parcela nº</Label>
                      <Select value={numeroParcela} onValueChange={setNumeroParcela}>
                        <SelectTrigger id="numero-parcela" className="mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[...Array(12)].map((_, i) => (
                            <SelectItem key={i + 1} value={(i + 1).toString()}>
                              {i + 1 === 1 ? `${i + 1} - Entrada` : `${i + 1}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="total-parcelas">Total de parcelas</Label>
                      <Select value={totalParcelas} onValueChange={setTotalParcelas}>
                        <SelectTrigger id="total-parcelas" className="mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[...Array(12)].map((_, i) => (
                            <SelectItem key={i + 1} value={(i + 1).toString()}>
                              {i + 1}x
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="descricao">Descrição (opcional)</Label>
                    <Input
                      id="descricao"
                      placeholder="Ex: Parcela 1 de 6 - Entrada"
                      value={descricao}
                      onChange={(e) => setDescricao(e.target.value)}
                      className="mt-2"
                    />
                  </div>

                  <Card className="bg-yellow-50 border-yellow-200">
                    <CardContent className="pt-4 flex gap-2">
                      <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                      <div className="text-sm text-yellow-800">
                        <strong>Lembrete:</strong> Você está emitindo a parcela <strong>{numeroParcela} de {totalParcelas}</strong>
                      </div>
                    </CardContent>
                  </Card>
                </CardContent>
              </Card>

              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => setStep('dados_fiscais')}
                  className="w-full"
                >
                  Voltar
                </Button>
                <Button 
                  onClick={() => setStep('confirmacao')}
                  className="w-full"
                >
                  Próximo: Confirmar
                </Button>
              </div>
            </div>
          )}

          {/* STEP 5: CONFIRMAÇÃO */}
          {step === 'confirmacao' && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Resumo da Nota Fiscal</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Cliente:</span>
                      <div className="font-semibold">{clienteEditar.nome}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Tipo de nota:</span>
                      <div className="font-semibold">
                        {tipoNota === 'NFSe'
                          ? 'NFS-e (Serviços)'
                          : tipoNota === 'NFe'
                          ? 'NF-e (Modelo 55)'
                          : 'NFC-e (Modelo 65)'}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">CPF/CNPJ:</span>
                      <div className="font-semibold">{clienteEditar.cpf_cnpj}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">{tipoNota === 'NFSe' ? 'Serviço' : 'Produto'}:</span>
                      <div className="font-semibold">{itemSelecionado?.nome}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Quantidade:</span>
                      <div className="font-semibold">{quantidade} UN</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Valor Unitário:</span>
                      <div className="font-semibold">{formatCurrency({ value: parseFloat(valorUnitario || '0') })}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Valor Total:</span>
                      <div className="font-bold text-green-600 text-lg">{formatCurrency({ value: calcularValorTotal() })}</div>
                    </div>
                    <div className="col-span-2 border-t pt-4">
                      <span className="text-gray-600">Parcela:</span>
                      <div className="font-semibold">{numeroParcela} de {totalParcelas}</div>
                    </div>
                  </div>

                  {tipoNota === 'NFSe' && (
                    <div className="border-t pt-4 space-y-2 text-sm">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-gray-600">Município prestador (IBGE):</span>
                          <div className="font-semibold">{nfseDados.prestadorCodigoMunicipio}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Município tomador (IBGE):</span>
                          <div className="font-semibold">{nfseDados.tomadorCodigoMunicipio}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Município prestação (IBGE):</span>
                          <div className="font-semibold">{nfseDados.codigoMunicipioPrestacao || nfseDados.tomadorCodigoMunicipio}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Item lista serviço:</span>
                          <div className="font-semibold">{nfseDados.itemListaServico}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Código tributário:</span>
                          <div className="font-semibold">{nfseDados.codigoTributarioMunicipio || '—'}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Alíquota ISS:</span>
                          <div className="font-semibold">{nfseDados.aliquota}%</div>
                        </div>
                        <div>
                          <span className="text-gray-600">ISS retido:</span>
                          <div className="font-semibold">{nfseDados.issRetido ? 'Sim' : 'Não'}</div>
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-600">Discriminação:</span>
                        <div className="font-semibold">{nfseDados.discriminacao}</div>
                      </div>
                    </div>
                  )}

                  {descricao && (
                    <div className="border-t pt-4">
                      <span className="text-gray-600">Descrição:</span>
                      <div className="font-semibold">{descricao}</div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => setStep('parcela')}
                  className="w-full"
                >
                  Voltar
                </Button>
                <Button 
                  onClick={handleEmitir}
                  disabled={emitindo}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  {emitindo ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Emitindo...
                    </>
                  ) : (
                    'Emitir Nota Fiscal'
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
